import { useState, useCallback } from 'react'
import {
  Download,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { DeleteDialog } from '@/components/ConfirmDialog'
import { cn } from '@/lib/utils'
import type { Image } from '@/types'

export interface ImageGalleryProps {
  /** Images to display. Confirmed images render as thumbnails; pending show a
   *  processing state; failed show an error state. */
  images: Image[]
  /** Whether images can be deleted (respects edit window rules) */
  canDelete?: boolean
  /** Callback when an image is deleted */
  onDelete?: (imageId: string) => void
  /** Whether delete is in progress */
  isDeleting?: boolean
}

/**
 * Trigger a download of the image.
 */
async function downloadImage(url: string, filename: string) {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Failed to download image:', error)
    // Fallback: open in new tab
    window.open(url, '_blank')
  }
}

export function ImageGallery({
  images,
  canDelete = false,
  onDelete,
  isDeleting = false,
}: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [deleteImage, setDeleteImage] = useState<Image | null>(null)

  // Confirmed images (with download URL) are the only ones the lightbox can navigate.
  const confirmedImages = images.filter(
    (img) => img.status === 'confirmed' && img.download_url
  )

  const handlePrevious = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1)
    }
  }, [selectedIndex])

  const handleNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < confirmedImages.length - 1) {
      setSelectedIndex(selectedIndex + 1)
    }
  }, [selectedIndex, confirmedImages.length])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      } else if (e.key === 'Escape') {
        setSelectedIndex(null)
      }
    },
    [handlePrevious, handleNext]
  )

  const handleDelete = useCallback(() => {
    if (deleteImage && onDelete) {
      onDelete(deleteImage.id)
    }
    setDeleteImage(null)
  }, [deleteImage, onDelete])

  const handleDownload = useCallback(
    (image: Image, e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (!image.download_url) return
      const filename = `image-${image.id}.jpg`
      downloadImage(image.download_url, filename)
    },
    []
  )

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <ImageIcon className="h-12 w-12 mb-2" />
        <p className="text-sm">No images attached to this report</p>
      </div>
    )
  }

  const selectedImage = selectedIndex !== null ? confirmedImages[selectedIndex] : null

  return (
    <>
      {/* Thumbnail Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {images.map((image) => {
          if (image.status === 'pending') {
            return (
              <Card
                key={image.id}
                className="relative overflow-hidden aspect-square flex flex-col items-center justify-center gap-2 bg-muted/40 text-muted-foreground"
                aria-label="Image processing"
              >
                {image.download_url ? (
                  <img
                    src={image.download_url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                  />
                ) : null}
                <div className="relative flex flex-col items-center gap-2 text-center px-2">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-xs font-medium">Processing…</span>
                </div>
              </Card>
            )
          }

          if (image.status === 'failed') {
            return (
              <Card
                key={image.id}
                className="relative overflow-hidden aspect-square flex flex-col items-center justify-center gap-2 border-destructive/30 bg-destructive/5 text-destructive"
                aria-label="Image upload failed"
              >
                <AlertTriangle className="h-6 w-6" />
                <span className="text-xs font-medium text-center px-2">
                  Upload failed
                </span>
              </Card>
            )
          }

          // Confirmed images may still be missing download_url in rare cases
          // (e.g. expired URL); treat as processing rather than showing a broken image.
          if (!image.download_url) {
            return (
              <Card
                key={image.id}
                className="relative overflow-hidden aspect-square flex flex-col items-center justify-center gap-2 bg-muted/40 text-muted-foreground"
                aria-label="Image loading"
              >
                <Loader2 className="h-6 w-6 animate-spin" />
              </Card>
            )
          }

          const confirmedIndex = confirmedImages.findIndex((c) => c.id === image.id)
          return (
            <Card
              key={image.id}
              className="relative overflow-hidden aspect-square cursor-pointer group"
              onClick={() => setSelectedIndex(confirmedIndex)}
            >
              <img
                src={image.download_url}
                alt={`Report image ${confirmedIndex + 1}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => handleDownload(image, e)}
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                {canDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteImage(image)
                    }}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          )
        })}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-black/95"
          showCloseButton={false}
          onKeyDown={handleKeyDown}
        >
          {selectedImage && (
            <div className="relative flex flex-col h-full">
              {/* Top bar with actions */}
              <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
                <span className="text-white text-sm">
                  {selectedIndex !== null ? selectedIndex + 1 : 0} / {confirmedImages.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={() => handleDownload(selectedImage)}
                    title="Download"
                  >
                    <Download className="h-5 w-5" />
                  </Button>
                  {canDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-red-500/50"
                      onClick={() => setDeleteImage(selectedImage)}
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={() => setSelectedIndex(null)}
                    title="Close"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Image */}
              <div className="flex items-center justify-center flex-1 p-4 pt-16 pb-4">
                <img
                  src={selectedImage.download_url}
                  alt={`Report image ${selectedIndex !== null ? selectedIndex + 1 : 0}`}
                  className="max-w-full max-h-[calc(90vh-8rem)] object-contain"
                />
              </div>

              {/* Navigation arrows */}
              {confirmedImages.length > 1 && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12',
                      selectedIndex === 0 && 'opacity-30 pointer-events-none'
                    )}
                    onClick={handlePrevious}
                    disabled={selectedIndex === 0}
                    title="Previous"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12',
                      selectedIndex === confirmedImages.length - 1 && 'opacity-30 pointer-events-none'
                    )}
                    onClick={handleNext}
                    disabled={selectedIndex === confirmedImages.length - 1}
                    title="Next"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteImage !== null}
        onOpenChange={(open) => !open && setDeleteImage(null)}
        itemName="this image"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </>
  )
}
